import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ProductDetailsModal from '../../components/ProductDetailsModal';
import LoginModal from '../../components/LoginModal/LoginModal';
import { useInquiry } from '../../context/InquiryContext';
import { useAuth } from '../../context/AuthContext';
import { getPlanConfig, shouldShowVerifiedBadge } from '../../utils/membershipUtils';
import { API_BASE_URL } from '../../config/api';
import './ProductListingPage.css';

const ProductListingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const categoryId = searchParams.get('category');
  const subcategorySlug = searchParams.get('subcategory');
  const searchQuery = searchParams.get('search');
  const brandIdFromUrl = searchParams.get('brand');

  console.log('ProductListingPage - searchQuery:', searchQuery, 'categoryId:', categoryId, 'subcategorySlug:', subcategorySlug);

  // Auth context
  const { isAuthenticated } = useAuth();

  // Inquiry context
  const { addToInquiry, isInInquiry, removeFromInquiry } = useInquiry();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentSubcategory, setCurrentSubcategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductDetailsModal, setShowProductDetailsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addedProducts, setAddedProducts] = useState({});
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  const [filterCounts, setFilterCounts] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Filter selections state - initialize brands from URL if present
  const [selectedFilters, setSelectedFilters] = useState(() => {
    const initialBrands = [];
    // Get brand from URL on initial load
    const urlParams = new URLSearchParams(window.location.search);
    const brandFromUrl = urlParams.get('brand');
    if (brandFromUrl) {
      initialBrands.push(parseInt(brandFromUrl));
    }
    return {
      brands: initialBrands,
      membership: [],
      verification: [],
      country: [],
      businessType: []
    };
  });

  const membershipOptions = [
    { name: 'Basic', value: 'Basic', icon: 'https://api.builder.io/api/v1/image/assets/TEMP/d8ee44da49d1e0c28cf5bfe995ca6aa7f62420ea?width=44' },
    { name: 'GCC', value: 'GCC', icon: 'https://api.builder.io/api/v1/image/assets/TEMP/cdcdf2f44d98a4cc6cdc74b524bc4359f623ed6c?width=44' },
    { name: 'Global', value: 'Global', icon: 'https://api.builder.io/api/v1/image/assets/TEMP/050559d54da8d35ba6d11841efdb737d4c3ae660?width=44' },
  ];

  const verificationOptions = [
    { name: 'Verified', value: 'verified', type: 'verified' },
    { name: 'Trusted', value: 'trusted', type: 'trusted' },
    { name: 'Audited', value: 'audited', type: 'audited' },
  ];

  const defaultCountryOptions = [
    { name: 'Saudi Arabia', flag: 'https://flagcdn.com/w80/sa.png' },
    { name: 'UAE', flag: 'https://flagcdn.com/w80/ae.png' },
    { name: 'Qatar', flag: 'https://flagcdn.com/w80/qa.png' },
    { name: 'Bahrain', flag: 'https://flagcdn.com/w80/bh.png' },
    { name: 'Jordan', flag: 'https://flagcdn.com/w80/jo.png' },
    { name: 'Kuwait', flag: 'https://flagcdn.com/w80/kw.png' },
    { name: 'Oman', flag: 'https://flagcdn.com/w80/om.png' },
  ];

  const businessTypeOptions = [
    { name: 'Retailer', value: 'Retailer' },
    { name: 'Exporter', value: 'Exporter' },
    { name: 'Manufacturer', value: 'Manufacturer' },
  ];

  // Toggle filter selection
  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => {
      const currentValues = prev[filterType];
      const isSelected = currentValues.includes(value);
      
      return {
        ...prev,
        [filterType]: isSelected 
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value]
      };
    });
  };

  // Check if a filter is selected
  const isFilterSelected = (filterType, value) => {
    return selectedFilters[filterType].includes(value);
  };

  // Get count for a specific filter option
  const getFilterCount = (filterType, value) => {
    if (!filterCounts) return 0;
    
    if (filterType === 'membership' && filterCounts.membership) {
      const found = filterCounts.membership.find(m => m.membershipPlan === value);
      return found ? found._count.membershipPlan : 0;
    }
    if (filterType === 'country' && filterCounts.countries) {
      const found = filterCounts.countries.find(c => c.country === value);
      return found ? found._count.country : 0;
    }
    if (filterType === 'brands' && filterCounts.brands) {
      const found = filterCounts.brands.find(b => b.brandId === value);
      return found ? found._count.brandId : 0;
    }
    if (filterType === 'businessType' && filterCounts.businessTypes) {
      const found = filterCounts.businessTypes.find(b => b.businessType === value);
      return found ? found._count.businessType : 0;
    }
    return 0;
  };

  // Fetch products with filters
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      let productUrl = `${API_BASE_URL}/products?`;
      if (categoryId) productUrl += `category=${categoryId}&`;
      if (subcategorySlug) productUrl += `subcategory=${subcategorySlug}&`;
      if (searchQuery) productUrl += `search=${encodeURIComponent(searchQuery)}&`;
      
      // Add filter parameters - use brandIdFromUrl directly if selectedFilters.brands is empty
      const brandsToFilter = selectedFilters.brands.length > 0 
        ? selectedFilters.brands 
        : (brandIdFromUrl ? [parseInt(brandIdFromUrl)] : []);
      
      if (brandsToFilter.length > 0) {
        productUrl += `brands=${brandsToFilter.join(',')}&`;
      }
      if (selectedFilters.membership.length > 0) {
        productUrl += `membership=${selectedFilters.membership.join(',')}&`;
      }
      if (selectedFilters.verification.length > 0) {
        productUrl += `verification=${selectedFilters.verification.join(',')}&`;
      }
      if (selectedFilters.country.length > 0) {
        productUrl += `country=${selectedFilters.country.join(',')}&`;
      }
      if (selectedFilters.businessType.length > 0) {
        productUrl += `businessType=${selectedFilters.businessType.join(',')}&`;
      }
      
      productUrl += 'limit=100';
      
      console.log('Fetching products with URL:', productUrl);

      const prodRes = await fetch(productUrl);
      if (prodRes.ok) {
        const prodData = await prodRes.json();
        setProducts(prodData.products || prodData || []);
        if (prodData.filterCounts) {
          setFilterCounts(prodData.filterCounts);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [categoryId, subcategorySlug, searchQuery, selectedFilters, brandIdFromUrl]);

  // Fetch categories and brands (initial load)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch categories
        const catRes = await fetch(`${API_BASE_URL}/categories`);
        if (catRes.ok) {
          const catData = await catRes.json();
          setCategories(catData);
          
          // Find current category
          if (categoryId) {
            const foundCat = catData.find(c => c.id === parseInt(categoryId) || c.slug === categoryId);
            if (foundCat) {
              setCurrentCategory(foundCat);
              setSubcategories(foundCat.subcategories || []);
              
              // Find current subcategory
              if (subcategorySlug && foundCat.subcategories) {
                const foundSub = foundCat.subcategories.find(s => s.slug === subcategorySlug || s.name === subcategorySlug);
                setCurrentSubcategory(foundSub || null);
              }
            }
          }
        }

        // Fetch brands
        const brandsRes = await fetch(`${API_BASE_URL}/brands`);
        if (brandsRes.ok) {
          const brandsData = await brandsRes.json();
          setBrands(brandsData);
        }

        // Fetch locations/countries
        const locRes = await fetch(`${API_BASE_URL}/locations`);
        if (locRes.ok) {
          const locData = await locRes.json();
          setCountries(locData);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };

    fetchInitialData();
  }, [categoryId, subcategorySlug]);

  // Update brand filter when URL brand parameter changes
  useEffect(() => {
    if (brandIdFromUrl) {
      const brandId = parseInt(brandIdFromUrl);
      setSelectedFilters(prev => {
        // Only update if brand is not already in the list
        if (!prev.brands.includes(brandId)) {
          return {
            ...prev,
            brands: [brandId]
          };
        }
        return prev;
      });
    }
  }, [brandIdFromUrl]);

  // Fetch products when filters change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const [expandedFilters, setExpandedFilters] = useState({
    brand: true,
    membership: true,
    verification: true,
    country: true,
    businessType: false
  });

  // Mobile sidebar toggle state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const toggleFilter = (filterName) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  // Handle inquire button click
  const handleInquireClick = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if user is logged in first
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    const seller = product.seller || {};
    const productData = {
      id: product.id,
      image: product.image || "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500",
      description: product.description || product.name,
      priceMin: product.priceMin || 0,
      priceMax: product.priceMax || 0,
      priceUnit: 'USD',
      pricePer: product.unit || 'Unit',
      moq: product.moq || '1 Unit',
      companyName: seller.name || product.companyName || 'Unknown Seller',
      sellerId: seller.userId || product.userId,
      sellerName: seller.name || product.companyName || 'Unknown Seller',
      sellerAvatar: seller.logo || 'https://via.placeholder.com/52',
      country: seller.country || product.country || 'UAE',
      countryFlag: seller.countryFlag || 'https://flagcdn.com/w80/ae.png'
    };
    
    if (isInInquiry(product.id)) {
      removeFromInquiry(product.id);
      setAddedProducts(prev => ({ ...prev, [product.id]: false }));
    } else {
      addToInquiry(productData);
      setAddedProducts(prev => ({ ...prev, [product.id]: true }));
      setTimeout(() => {
        setAddedProducts(prev => ({ ...prev, [product.id]: 'done' }));
      }, 2000);
    }
  };

  // Handle view mobile button click - navigate to product details page
  const handleViewMobileClick = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/product/${product.id}`);
  };

  const handleSubcategoryClick = (sub) => {
    setIsMobileSidebarOpen(false); // Close mobile sidebar
    navigate(`/products?category=${categoryId}&subcategory=${sub.slug}`);
  };

  const VerifiedBadge = () => (
    <svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22 15C22 15.9323 20.7329 16.6871 20.5034 17.5472C20.2653 18.4377 20.9782 19.7221 20.528 20.5015C20.0705 21.2923 18.597 21.314 17.9562 21.9562C17.314 22.5984 17.2923 24.0705 16.5014 24.528C15.7221 24.9782 14.4377 24.2653 13.5472 24.5034C12.6871 24.7329 11.9337 26 11 26C10.0663 26 9.31291 24.7329 8.45276 24.5034C7.56231 24.2653 6.27788 24.9782 5.49856 24.528C4.70769 24.0705 4.68605 22.597 4.04382 21.9562C3.4016 21.3154 1.92954 21.2923 1.47205 20.5015C1.02177 19.7221 1.73472 18.4377 1.49659 17.5472C1.26712 16.6871 0 15.9338 0 15C0 14.0663 1.26712 13.3129 1.49659 12.4528C1.73472 11.5623 1.02177 10.2779 1.47205 9.49857C1.92954 8.7077 3.40305 8.68605 4.04382 8.04383C4.68605 7.40161 4.70769 5.92955 5.49856 5.47206C6.27788 5.02178 7.56231 5.73472 8.45276 5.49659C9.31291 5.26712 10.0663 4 11 4C11.9337 4 12.6871 5.26712 13.5472 5.49659C14.4377 5.73472 15.7221 5.02178 16.5014 5.47206C17.2923 5.92955 17.314 7.40305 17.9562 8.04383C18.5984 8.68461 20.0705 8.7077 20.528 9.49857C20.9782 10.2779 20.2653 11.5623 20.5034 12.4528C20.7329 13.3129 22 14.0663 22 15Z" fill="#29ABE2"/>
      <path d="M16.5881 11.6015C16.1234 11.1368 15.3701 11.1368 14.9054 11.6015L10.0317 16.4751L8.00835 14.4518C7.54364 13.9871 6.79029 13.9871 6.32558 14.4518C5.86087 14.9165 5.86087 15.6698 6.32558 16.1345L9.14847 18.9574C9.39237 19.2013 9.7142 19.3168 10.0331 19.3052C10.3521 19.3168 10.6739 19.2013 10.9178 18.9574L16.5896 13.2857C17.0543 12.821 17.0543 12.0676 16.5896 11.6029L16.5881 11.6015Z" fill="white"/>
    </svg>
  );

  const TrustedBadge = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9.71649 18.8752C5.62234 18.8752 2.29102 15.5438 2.29102 11.4495C2.29102 7.35536 5.62234 4.02373 9.71649 4.02373C13.8106 4.02373 17.1422 7.35536 17.1422 11.4495C17.1422 15.5436 13.8106 18.8752 9.71649 18.8752Z" fill="#760BA8"/>
      <path d="M9.72019 17.2156C12.8289 17.2156 15.3491 14.6415 15.3491 11.4662C15.3491 8.2909 12.8289 5.7168 9.72019 5.7168C6.61145 5.7168 4.09131 8.2909 4.09131 11.4662C4.09131 14.6415 6.61145 17.2156 9.72019 17.2156Z" fill="#0EC936"/>
      <path d="M20.9429 21.2726C20.8308 20.7414 20.5561 20.2494 20.1468 19.8527L17.4501 17.3326C18.6947 15.6997 19.4348 13.6618 19.4348 11.4557C19.4348 6.0973 15.0757 1.73828 9.71738 1.73828C4.35901 1.73828 0 6.0973 0 11.4557C0 16.814 4.35901 21.1731 9.71738 21.1731C11.3811 21.1731 12.9481 20.7526 14.3189 20.0091L17.0809 22.5912C17.4753 22.9692 17.9785 23.2165 18.5168 23.2961C19.0552 23.3758 19.6054 23.2845 20.0857 23.0335C20.5659 22.7825 20.9521 22.3836 21.1905 21.8934C21.4289 21.4032 21.5065 20.8474 21.4129 20.3083L20.9429 21.2726Z" fill="#760BA8"/>
    </svg>
  );

  const AuditedBadge = () => (
    <svg width="18" height="24" viewBox="0 0 18 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15.6913 0.0199925C12.5646 -0.0092276 13.6192 -0.0384516 14.711 0.182027C15.7683 0.555247 17.5602 1.80508 18.996 2.98052C20.1649 4.55177 21.7283 6.47098 22.9423 8.53233L23.6769 8.90423L23.8097 9.27611C23.6769 10.6587 23.1841 11.9657 22.4841 13.1611L21.5981 14.3166C20.7414 15.3406 19.7292 16.2053 18.5922 16.9889C17.5615 17.471 16.4494 11.9165 9.00654 12.1636L8.62536 12.1742C8.38894 11.9405 5.70601 9.28392 5.47358 9.05412L5.103 9.05943L4.87721 9.29454L3.38433 10.8526C3.15854 11.089 3.16254 11.4663 3.39497 11.6961L8.52043 16.7727C8.75685 17.0078 9.13803 16.9958 9.36117 16.7488L15.5837 9.8657L16.9584 8.34484L17.8456 7.36324C18.0648 7.1215 18.0488 6.7478 17.8102 6.5256L15.6913 0.0199925Z" fill="#0EC936"/>
    </svg>
  );

  const Checkbox = ({ checked = false }) => (
    checked ? (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="3" fill="#1F396C"/>
        <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M18 4.5H6C5.17157 4.5 4.5 5.17157 4.5 6V18C4.5 18.8284 5.17157 19.5 6 19.5H18C18.8284 19.5 19.5 18.8284 19.5 18V6C19.5 5.17157 18.8284 4.5 18 4.5ZM6 3C4.34315 3 3 4.34315 3 6V18C3 19.6569 4.34315 21 6 21H18C19.6569 21 21 19.6569 21 18V6C21 4.34315 19.6569 3 18 3H6Z" fill="#6D6D6D"/>
      </svg>
    )
  );

  const ChevronUp = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 13.5L12 8.5L17 13.5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ChevronDown = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 10.5L12 15.5L7 10.5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ChevronRight = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="product-listing-page">
      <Header />
      
      <main className="plp-main-content">
        {/* Breadcrumb */}
        <div className="plp-breadcrumb">
          <span className="breadcrumb-item" onClick={() => navigate('/')}>Home</span>
          <ChevronRight />
          {currentCategory && !searchQuery && (
            <>
              <span className="breadcrumb-item" onClick={() => navigate(`/products?category=${currentCategory.id}`)}>
                {currentCategory.name}
              </span>
              <ChevronRight />
            </>
          )}
          <span className="breadcrumb-item active">
            {searchQuery ? `Search: "${searchQuery}"` : (currentSubcategory?.name || currentCategory?.name || 'Products')}
          </span>
        </div>

        <div className="plp-container">
          {/* Mobile Filter Button */}
          <button 
            className="mobile-filter-btn" 
            onClick={() => setIsMobileSidebarOpen(true)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21.6197 4.64346C21.5043 4.37675 21.313 4.14986 21.0696 3.99101C20.8263 3.83216 20.5416 3.75 20.251 3.75H3.75103C3.46053 3.75 3.17591 3.83213 2.9326 3.99093C2.68929 4.14974 2.49804 4.37656 2.38266 4.64319C2.26728 4.90982 2.23277 5.20434 2.28316 5.49084C2.33354 5.77735 2.46653 6.04338 2.66603 6.2572L2.66759 6.2588L9.00103 13.0472V19.4993C9.00083 19.7522 9.06175 20.0015 9.17862 20.2257C9.29549 20.4499 9.46477 20.6422 9.67228 20.7863C9.87979 20.9304 10.1194 21.0221 10.3712 21.0537C10.6229 21.0852 10.8787 21.0558 11.1134 20.968L11.1159 20.9671L14.1159 19.9021C14.4014 19.7942 14.6488 19.6047 14.8277 19.357C15.0066 19.1094 15.1093 18.8144 15.1243 18.5088L15.126 18.4722V13.0472L21.336 6.26721L21.3435 6.25877C21.5408 6.04489 21.6707 5.77764 21.7171 5.49037C21.7634 5.2031 21.7241 4.90854 21.6041 4.64346H21.6197Z" fill="#fff"/>
            </svg>
            Filter By
          </button>

          {/* Mobile Sidebar Overlay */}
          {isMobileSidebarOpen && (
            <div 
              className="mobile-sidebar-overlay active" 
              onClick={() => setIsMobileSidebarOpen(false)}
            />
          )}

          {/* Left Sidebar */}
          <aside className={`plp-sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}`}>
            {/* Mobile Close Button */}
            {isMobileSidebarOpen && (
              <button 
                onClick={() => setIsMobileSidebarOpen(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '16px',
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 1001,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            {/* Category Section */}
            <div className="sidebar-section category-section">
              <div className="category-header">
                <span className="category-title">{currentCategory?.name || 'Categories'}</span>
              </div>
              <div className="category-list">
                {subcategories.map((sub) => (
                  <div 
                    key={sub.id} 
                    className={`category-item ${currentSubcategory?.slug === sub.slug ? 'active' : ''}`}
                    onClick={() => handleSubcategoryClick(sub)}
                  >
                    <span>{sub.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Filter By Header */}
            <div className="filter-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21.6197 4.64346C21.5043 4.37675 21.313 4.14986 21.0696 3.99101C20.8263 3.83216 20.5416 3.75 20.251 3.75H3.75103C3.46053 3.75 3.17591 3.83213 2.9326 3.99093C2.68929 4.14974 2.49804 4.37656 2.38266 4.64319C2.26728 4.90982 2.23277 5.20434 2.28316 5.49084C2.33354 5.77735 2.46653 6.04338 2.66603 6.2572L2.66759 6.2588L9.00103 13.0472V19.4993C9.00083 19.7522 9.06175 20.0015 9.17862 20.2257C9.29549 20.4499 9.46477 20.6422 9.67228 20.7863C9.87979 20.9304 10.1194 21.0221 10.3712 21.0537C10.6229 21.0852 10.8787 21.0558 11.1134 20.968L11.1159 20.9671L14.1159 19.9021C14.4014 19.7942 14.6488 19.6047 14.8277 19.357C15.0066 19.1094 15.1093 18.8144 15.1243 18.5088L15.126 18.4722V13.0472L21.336 6.26721L21.3435 6.25877C21.5408 6.04489 21.6707 5.77764 21.7171 5.49037C21.7634 5.2031 21.7241 4.90854 21.6041 4.64346H21.6197ZM13.7053 12.2419C13.5756 12.3795 13.5026 12.5609 13.501 12.75V18.2494L10.501 19.4143V12.75C10.501 12.5596 10.4286 12.3762 10.2985 12.2372L3.75095 5.25002H20.251L13.7053 12.2419Z" fill="#212121"/>
              </svg>
              <span className="filter-title">Filter By</span>
            </div>

            {/* Brand Filter */}
            <div className="filter-section">
              <div className="filter-section-header" onClick={() => toggleFilter('brand')}>
                <span className="filter-section-title">Brand</span>
                {expandedFilters.brand ? <ChevronUp /> : <ChevronDown />}
              </div>
              {expandedFilters.brand && (
                <>
                  <div className="filter-search">
                    <input 
                      type="text" 
                      placeholder="Search brand" 
                      className="filter-search-input"
                      value={brandSearchQuery}
                      onChange={(e) => setBrandSearchQuery(e.target.value)}
                    />
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 14L10 10M2 6.66667C2 7.2795 2.12071 7.88634 2.35523 8.45252C2.58975 9.01871 2.93349 9.53316 3.36683 9.9665C3.80018 10.3998 4.31462 10.7436 4.88081 10.9781C5.447 11.2126 6.05383 11.3333 6.66667 11.3333C7.2795 11.3333 7.88634 11.2126 8.45252 10.9781C9.01871 10.7436 9.53316 10.3998 9.9665 9.9665C10.3998 9.53316 10.7436 9.01871 10.9781 8.45252C11.2126 7.88634 11.3333 7.2795 11.3333 6.66667C11.3333 6.05383 11.2126 5.447 10.9781 4.88081C10.7436 4.31462 10.3998 3.80018 9.9665 3.36683C9.53316 2.93349 9.01871 2.58975 8.45252 2.35523C7.88634 2.12071 7.2795 2 6.66667 2C6.05383 2 5.447 2.12071 4.88081 2.35523C4.31462 2.58975 3.80018 2.93349 3.36683 3.36683C2.93349 3.80018 2.58975 4.31462 2.35523 4.88081C2.12071 5.447 2 6.05383 2 6.66667Z" stroke="#6D6D6D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="filter-options">
                    {brands
                      .filter(brand => brand.name.toLowerCase().includes(brandSearchQuery.toLowerCase()))
                      .filter(brand => {
                        // Only show brands that have products in the current category/subcategory context
                        const count = getFilterCount('brands', brand.id);
                        return count > 0 || isFilterSelected('brands', brand.id);
                      })
                      .slice(0, 8)
                      .map((brand) => (
                        <div 
                          key={brand.id} 
                          className={`filter-option ${isFilterSelected('brands', brand.id) ? 'selected' : ''}`}
                          onClick={() => handleFilterChange('brands', brand.id)}
                        >
                          <Checkbox checked={isFilterSelected('brands', brand.id)} />
                          <span className="option-name">{brand.name}</span>
                          {getFilterCount('brands', brand.id) > 0 && (
                            <span className="option-count">({getFilterCount('brands', brand.id)})</span>
                          )}
                        </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="filter-divider"></div>

            {/* Membership Filter */}
            <div className="filter-section">
              <div className="filter-section-header" onClick={() => toggleFilter('membership')}>
                <span className="filter-section-title">Membership</span>
                {expandedFilters.membership ? <ChevronUp /> : <ChevronDown />}
              </div>
              {expandedFilters.membership && (
                <div className="filter-options">
                  {membershipOptions.map((option, index) => (
                    <div 
                      key={index} 
                      className={`filter-option ${isFilterSelected('membership', option.value) ? 'selected' : ''}`}
                      onClick={() => handleFilterChange('membership', option.value)}
                    >
                      <Checkbox checked={isFilterSelected('membership', option.value)} />
                      <div className="membership-option">
                        <img src={option.icon} alt={option.name} className="membership-icon" />
                        <span className="option-name">{option.name}</span>
                        <span className="option-count">({getFilterCount('membership', option.value)})</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-divider"></div>

            {/* Verification Filter */}
            <div className="filter-section">
              <div className="filter-section-header" onClick={() => toggleFilter('verification')}>
                <span className="filter-section-title">Verification Levels</span>
                {expandedFilters.verification ? <ChevronUp /> : <ChevronDown />}
              </div>
              {expandedFilters.verification && (
                <div className="filter-options">
                  <div 
                    className={`filter-option ${isFilterSelected('verification', 'verified') ? 'selected' : ''}`}
                    onClick={() => handleFilterChange('verification', 'verified')}
                  >
                    <Checkbox checked={isFilterSelected('verification', 'verified')} />
                    <div className="verification-option">
                      <VerifiedBadge />
                      <span className="verification-text verified">Verified</span>
                    </div>
                  </div>
                  <div 
                    className={`filter-option ${isFilterSelected('verification', 'trusted') ? 'selected' : ''}`}
                    onClick={() => handleFilterChange('verification', 'trusted')}
                  >
                    <Checkbox checked={isFilterSelected('verification', 'trusted')} />
                    <div className="verification-option">
                      <TrustedBadge />
                      <span className="verification-text trusted">Trusted</span>
                    </div>
                  </div>
                  <div 
                    className={`filter-option ${isFilterSelected('verification', 'audited') ? 'selected' : ''}`}
                    onClick={() => handleFilterChange('verification', 'audited')}
                  >
                    <Checkbox checked={isFilterSelected('verification', 'audited')} />
                    <div className="verification-option">
                      <AuditedBadge />
                      <span className="verification-text audited">Audited</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="filter-divider"></div>

            {/* Country Filter */}
            <div className="filter-section">
              <div className="filter-section-header" onClick={() => toggleFilter('country')}>
                <span className="filter-section-title">Country</span>
                {expandedFilters.country ? <ChevronUp /> : <ChevronDown />}
              </div>
              {expandedFilters.country && (
                <div className="filter-options">
                  {(countries.length > 0 ? countries : defaultCountryOptions).map((country, index) => (
                    <div 
                      key={index} 
                      className={`filter-option ${isFilterSelected('country', country.name) ? 'selected' : ''}`}
                      onClick={() => handleFilterChange('country', country.name)}
                    >
                      <Checkbox checked={isFilterSelected('country', country.name)} />
                      <div className="country-option">
                        <div className="country-flag-container">
                          <img src={country.flag || `https://flagcdn.com/w80/${country.code?.toLowerCase() || 'ae'}.png`} alt={country.name} className="country-flag" />
                        </div>
                        <span className="option-name">{country.name}</span>
                        <span className="option-count">({getFilterCount('country', country.name)})</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-divider"></div>

            {/* Business Type Filter */}
            <div className="filter-section">
              <div className="filter-section-header" onClick={() => toggleFilter('businessType')}>
                <span className="filter-section-title">Business Type</span>
                {expandedFilters.businessType ? <ChevronUp /> : <ChevronDown />}
              </div>
              {expandedFilters.businessType && (
                <div className="filter-options">
                  {businessTypeOptions.map((type, index) => (
                    <div 
                      key={index} 
                      className={`filter-option ${isFilterSelected('businessType', type.value) ? 'selected' : ''}`}
                      onClick={() => handleFilterChange('businessType', type.value)}
                    >
                      <Checkbox checked={isFilterSelected('businessType', type.value)} />
                      <span className="option-name">{type.name}</span>
                      <span className="option-count">({getFilterCount('businessType', type.value)})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <div className="plp-content">
            {/* Banner */}
            <div className="plp-banner">
              <img 
                src="https://api.builder.io/api/v1/image/assets/TEMP/659dc936c0063da65c0d0cd4f76e1a113d742af6?width=2144" 
                alt={`${currentCategory?.name || 'Products'} Banner`}
                className="plp-banner-image"
              />
            </div>

            {loading ? (
              <div className="plp-loading" style={{textAlign: 'center', padding: '40px'}}>
                <p>Loading products...</p>
              </div>
            ) : (
              <>
                {/* Products Grid */}
                <div className="plp-products-grid">
                  {products.slice(0, 4).map((product) => (
                    <ProductListCard 
                      key={product.id} 
                      product={product} 
                      navigate={navigate}
                      onInquireClick={handleInquireClick}
                      onViewMobileClick={handleViewMobileClick}
                      isInInquiry={isInInquiry}
                      addedProducts={addedProducts}
                    />
                  ))}
                </div>

                {/* Mid Banner */}
                <div className="plp-mid-banner">
                  <img 
                    src="https://api.builder.io/api/v1/image/assets/TEMP/c70e129afc69d7dc9464719e19866004ede27393?width=2144" 
                    alt="Promotional Banner" 
                    className="plp-mid-banner-image"
                  />
                </div>

                {/* More Products - Show all remaining products */}
                <div className="plp-products-grid">
                  {products.slice(4).map((product) => (
                    <ProductListCard 
                      key={product.id} 
                      product={product} 
                      navigate={navigate}
                      onInquireClick={handleInquireClick}
                      onViewMobileClick={handleViewMobileClick}
                      isInInquiry={isInInquiry}
                      addedProducts={addedProducts}
                    />
                  ))}
                </div>

                {products.length === 0 && (
                  <div className="plp-no-products" style={{textAlign: 'center', padding: '40px'}}>
                    <p>{searchQuery ? `No products found for "${searchQuery}"` : 'No products found in this category.'}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
      
      {/* Product Details Modal */}
      <ProductDetailsModal 
        isOpen={showProductDetailsModal} 
        onClose={() => {
          setShowProductDetailsModal(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
      />
      
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
    </div>
  );
};

// Product Card Component for Listing Page - Matching HomePage Featured Product Card Design
const ProductListCard = ({ product, navigate, onInquireClick, onViewMobileClick, isInInquiry, addedProducts }) => {
  const seller = product.seller || {};
  const isAdded = isInInquiry ? isInInquiry(product.id) : false;
  const showAdded = addedProducts?.[product.id] === true;
  
  // Use seller.name as the display name (seller profile has the real company name)
  const displayCompanyName = seller.name || 'Unknown Seller';
  const displayLogo = seller.logo || "https://via.placeholder.com/24x24?text=S";
  
  return (
    <div className="featured-product-card" onClick={() => navigate(`/product/${product.id}`)} style={{ cursor: 'pointer' }}>
      {/* Product Image Section */}
      <div className="featured-product-image-section">
        <img 
          src={product.image || "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500"} 
          alt={product.name} 
          className="featured-product-img"
        />
      </div>
      
      {/* Product Details Section */}
      <div className="featured-product-details-section" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Info Block */}
        <div className="featured-info-block">
          {/* Product Name Block */}
          <div className="featured-desc-block">
            <p className="featured-product-name">
              {product.name || 'Unknown Product'}
            </p>
            
            {/* Price Block */}
            <div className="featured-price-block">
              <div className="featured-price-row">
                <span className="featured-price-amount">${product.priceMin} - ${product.priceMax}</span>
                <span className="featured-price-unit">/ {product.unit || 'Tons'}</span>
              </div>
              <p className="featured-moq">
                <span className="featured-moq-label">(MOQ) </span>
                <span className="featured-moq-value">{product.moq} {product.unit || ''}</span>
              </p>
            </div>
          </div>
          
          {/* Badges Block */}
          <div className="featured-badges-block">
            <div className="featured-badges-row">
              {/* Membership Plan Badge */}
              {(() => {
                const planConfig = getPlanConfig(seller.membershipPlan);
                return (
                  <div className="featured-global-badge" style={{ borderColor: planConfig.color }}>
                    <img 
                      src={planConfig.icon} 
                      alt={planConfig.label} 
                      className="featured-global-icon"
                    />
                    <span className="featured-global-text" style={{ color: planConfig.color }}>{planConfig.label}</span>
                  </div>
                );
              })()}
              
              {/* Verified Badge - only show for GCC and Global plans (not Basic) */}
              {shouldShowVerifiedBadge(seller.membershipPlan) && (
                <div className="featured-verified-badge">
                  <svg width="16" height="16" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 11C22 11.9323 20.7329 12.6871 20.5034 13.5472C20.2653 14.4377 20.9782 15.7221 20.528 16.5015C20.0705 17.2923 18.597 17.314 17.9562 17.9562C17.314 18.5984 17.2923 20.0705 16.5014 20.528C15.7221 20.9782 14.4377 20.2653 13.5472 20.5034C12.6871 20.7329 11.9337 22 11 22C10.0663 22 9.31291 20.7329 8.45276 20.5034C7.56231 20.2653 6.27788 20.9782 5.49856 20.528C4.70769 20.0705 4.68605 18.597 4.04382 17.9562C3.4016 17.3154 1.92954 17.2923 1.47205 16.5015C1.02177 15.7221 1.73472 14.4377 1.49659 13.5472C1.26712 12.6871 0 11.9338 0 11C0 10.0663 1.26712 9.3129 1.49659 8.4528C1.73472 7.5623 1.02177 6.2779 1.47205 5.49857C1.92954 4.7077 3.40305 4.68605 4.04382 4.04383C4.68605 3.40161 4.70769 1.92955 5.49856 1.47206C6.27788 1.02178 7.56231 1.73472 8.45276 1.49659C9.31291 1.26712 10.0663 0 11 0C11.9337 0 12.6871 1.26712 13.5472 1.49659C14.4377 1.73472 15.7221 1.02178 16.5014 1.47206C17.2923 1.92955 17.314 3.40305 17.9562 4.04383C18.5984 4.68461 20.0705 4.7077 20.528 5.49857C20.9782 6.2779 20.2653 7.5623 20.5034 8.4528C20.7329 9.3129 22 10.0663 22 11Z" fill="#29ABE2"/>
                    <path d="M15.5876 7.6015C15.1229 7.1368 14.3696 7.1368 13.9049 7.6015L9.0312 12.4751L7.00786 10.4518C6.54315 9.9871 5.7898 9.9871 5.32509 10.4518C4.86039 10.9165 4.86039 11.6698 5.32509 12.1345L8.14798 14.9574C8.39188 15.2013 8.71372 15.3168 9.0327 15.3052C9.3516 15.3168 9.6734 15.2013 9.9173 14.9574L15.5891 9.2857C16.0538 8.821 16.0538 8.0676 15.5891 7.6029L15.5876 7.6015Z" fill="white"/>
                  </svg>
                  <span className="featured-verified-text">Verified</span>
                </div>
              )}
            </div>
            
            {/* Seller Company Name - Clickable to go to seller profile */}
            <div 
              className="featured-seller-company"
              onClick={(e) => {
                e.stopPropagation();
                if (seller.id) navigate(`/seller/${seller.id}`);
              }}
              style={{ cursor: seller.id ? 'pointer' : 'default' }}
              title={seller.id ? `View ${displayCompanyName} profile` : ''}
            >
              <img 
                src={displayLogo} 
                alt={displayCompanyName} 
                className="featured-company-logo"
              />
              <span className="featured-company-name">{displayCompanyName}</span>
            </div>
            
            {/* Location Row */}
            <div className="featured-location-row">
              <div className="featured-flag-wrapper">
                <img 
                  src={seller.countryFlag || "https://flagcdn.com/w80/ae.png"} 
                  alt="Flag" 
                  className="featured-flag-img"
                />
              </div>
              <span className="featured-location-text">
                <span className="featured-country">{(seller.country || 'UAE').replace('United Arab Emirates', 'UAE')} / </span>
                <span className="featured-years">{seller.memberYears || 1} Year{(seller.memberYears || 1) > 1 ? 's' : ''}</span>
              </span>
            </div>
            
            {/* Business Type */}
            <span className="featured-business-text">{seller.businessType || 'Manufacturer'}</span>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="featured-actions-row" style={{ marginTop: 'auto' }}>
          <button 
            className={`featured-btn-inquire ${isAdded ? 'added' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onInquireClick && onInquireClick(product, e);
            }}
          >
            {showAdded ? '✓ Added!' : isAdded ? 'Remove' : 'Add To Quotation'}
          </button>
          <button 
            className="featured-btn-call"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const phone = seller.phone || seller.phoneNumber || product.phone;
              if (phone) {
                window.location.href = `tel:${phone}`;
              } else {
                alert('Phone number not available for this seller');
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 16.92V19.92C22 20.4704 21.7893 20.9981 21.4142 21.3981C21.0391 21.7982 20.5304 22.0362 19.98 22.0699C19.643 22.0899 19.2989 22.0999 18.95 22.0999C10.21 22.0999 3.10001 14.9899 3.10001 6.24994C3.10001 5.90094 3.11001 5.55694 3.13001 5.21994C3.16376 4.66947 3.40177 4.16077 3.80183 3.78568C4.20189 3.41059 4.72954 3.19994 5.28001 3.19994H8.28001C8.72424 3.19557 9.15598 3.35553 9.49332 3.64969C9.83067 3.94386 10.0507 4.35171 10.11 4.79194C10.2199 5.66094 10.4293 6.51394 10.73 7.32994C10.8567 7.66661 10.8837 8.03161 10.808 8.38227C10.7323 8.73294 10.5571 9.05427 10.3 9.30994L9.00001 10.6099C10.3612 13.1755 12.4245 15.2387 14.99 16.5999L16.29 15.2999C16.5457 15.0429 16.8671 14.8677 17.2177 14.792C17.5684 14.7163 17.9334 14.7433 18.27 14.8699C19.086 15.1707 19.9391 15.3801 20.808 15.4899C21.2532 15.5498 21.6651 15.7746 21.9605 16.1183C22.256 16.462 22.4131 16.9009 22.4 17.3499L22 16.92Z" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Call</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductListingPage;
